import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Btn, EmptyState } from "../components/UI";
import { BulkOnboardButton } from "../components/BulkOnboardModal";
import { ClientPortalButton } from "../components/ClientPortalModal";
import { DigestPrefsButton } from "../components/DigestPrefsModal";
import { Landmark, Loader2, ShieldCheck, Plus, ArrowRight, Users, Wallet, HandCoins,
         AlertTriangle, Sparkles, CalendarClock } from "lucide-react";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export default function AgencyPortfolio() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState({});

  useEffect(() => {
    api.get("/political-os/agency/summary")
       .then(r => setData(r.data))
       .catch(() => setData({ totals: {}, entities: [], upcoming_elections: [] }));
    api.get("/political-os/agency/health")
       .then(r => setHealth(r.data.health || {}))
       .catch(() => {});
  }, []);

  const activate = async (id) => {
    await api.post(`/political-os/entities/${id}/activate`);
    toast.success("Organization activated");
    window.location.href = "/dashboard";
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  const t = data.totals || {};

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="agency-portfolio-page">
      <PageHeader eyebrow="Agency" title="My Clients"
        subtitle="Every candidate, PAC and committee you manage — races, spend, fundraising and pending approvals at a glance."
        action={<div className="flex items-center gap-2">
          <Btn variant="secondary" onClick={() => navigate("/approvals/launch")} data-testid="agency-launch-approvals-btn">
            <ShieldCheck className="w-4 h-4" /> Launch Approvals
          </Btn>
          <DigestPrefsButton clients={data?.entities || []} />
          <BulkOnboardButton onDone={() => window.location.reload()} />
          <Btn onClick={() => navigate("/onboarding/political")} data-testid="agency-add-client">
            <Plus className="w-4 h-4" /> Add client
          </Btn>
        </div>} />

      {/* Consolidated stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="agency-totals">
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400">Clients</div>
          <div className="mt-2 font-display text-3xl font-bold text-white flex items-center gap-2" data-testid="totals-entities">
            <Landmark className="w-5 h-5 text-cyan-300/70" /> {t.entities || 0}
          </div>
          <div className="mt-1 text-xs text-slate-500">Active organizations</div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400">Live campaigns</div>
          <div className="mt-2 font-display text-3xl font-bold text-white" data-testid="totals-campaigns">
            {t.active_campaigns || 0}<span className="text-slate-500 text-lg font-normal"> / {t.campaigns || 0}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">Across all clients</div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400">Committed budget</div>
          <div className="mt-2 font-display text-3xl font-bold text-white" data-testid="totals-budget">{usd(t.budget)}</div>
          <div className="mt-1 text-xs text-slate-500">Sum of all media plans</div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400">Raised this cycle</div>
          <div className="mt-2 font-display text-3xl font-bold text-white flex items-center gap-2" data-testid="totals-raised">
            <HandCoins className="w-5 h-5 text-emerald-300/70" /> {usd(t.raised)}
          </div>
          <div className="mt-1 text-xs text-emerald-300">{usd(t.raised_this_week)} this week</div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400">AI usage (month)</div>
          <div className="mt-2 font-display text-3xl font-bold text-white flex items-center gap-2" data-testid="totals-ai-cost">
            <Sparkles className="w-5 h-5 text-cyan-300/70" /> {usd(t.ai_this_month_cost)}
          </div>
          <div className="mt-1 text-xs text-slate-500">{t.ai_this_month_calls || 0} calls this month</div>
        </Card>
      </div>

      {t.pending_approvals > 0 && (
        <Card data-testid="agency-alerts" className="border-amber-400/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-white">
                {t.pending_approvals} approval{t.pending_approvals > 1 ? "s" : ""} pending across your clients
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Open the affected client to review — campaigns can&apos;t launch until their chain is complete.
              </div>
            </div>
          </div>
        </Card>
      )}

      {(data.upcoming_elections || []).length > 0 && (
        <Card data-testid="agency-upcoming">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-lg font-bold">Upcoming elections in the next 60 days</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.upcoming_elections.map(u => (
              <button
                key={u.entity_id}
                onClick={() => activate(u.entity_id)}
                data-testid={`upcoming-${u.entity_id}`}
                className="text-left p-3 rounded-lg border border-white/10 bg-white/[0.02] hover:border-cyan-400/40 hover:bg-cyan-400/[0.03] transition"
              >
                <div className="text-sm font-medium text-white truncate">{u.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{u.date}</div>
                <div className="text-xs mt-1"><span className="text-cyan-300 font-bold">{u.days_until}</span> <span className="text-slate-500">days out</span></div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Clients grid */}
      {(data.entities || []).length === 0 ? (
        <EmptyState icon={Landmark} title="No clients yet"
                    description="Register your first political entity or accept a team invite to build your portfolio." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="agency-clients-grid">
          {data.entities.map(p => (
            <Card key={p.id} className="flex flex-col gap-3" data-testid={`client-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold text-white truncate flex items-center gap-2">
                    {health[p.id] && (
                      <span
                        title={(health[p.id].signals || []).map(s => s.text).join("\n") || "Healthy"}
                        data-testid={`client-health-${p.id}`}
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          health[p.id].health === "red" ? "bg-rose-400" :
                          health[p.id].health === "yellow" ? "bg-amber-300" : "bg-emerald-400"
                        }`}
                      />
                    )}
                    <span className="truncate">{p.name}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {(p.entity_type || "").replace(/_/g, " ")}{p.state ? ` · ${p.state}` : ""}
                  </div>
                </div>
                {p.days_until != null && (
                  <div className="text-center shrink-0">
                    <div className={`font-display text-2xl font-bold ${p.days_until <= 30 ? "text-amber-300" : "text-cyan-300"}`}>{p.days_until}</div>
                    <div className="text-[8px] uppercase tracking-wider text-slate-500">days out</div>
                  </div>
                )}
              </div>
              {p.race_id && <div className="text-xs text-slate-400 truncate">{p.race_id} · {p.election_date || "date TBD"}</div>}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white/[0.03] border border-white/10 py-2">
                  <div className="text-sm font-bold text-white">{p.campaigns}</div>
                  <div className="text-[8px] uppercase text-slate-500">campaigns</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 py-2">
                  <div className="text-sm font-bold text-white">{usd(p.budget)}</div>
                  <div className="text-[8px] uppercase text-slate-500">budget</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 py-2">
                  <div className="text-sm font-bold text-emerald-300">{usd(p.raised)}</div>
                  <div className="text-[8px] uppercase text-slate-500">raised</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap mt-auto pt-1">
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${p.verification?.score >= 85 ? "border-emerald-400/40 text-emerald-300" : "border-amber-400/40 text-amber-300"}`}>
                  <ShieldCheck className="w-3 h-3" /> {p.verification?.score}%
                </span>
                {p.pending_approvals > 0 && (
                  <span className="text-[10px] text-amber-300" data-testid={`client-pending-${p.id}`}>
                    {p.pending_approvals} pending
                  </span>
                )}
                {p.unique_donors > 0 && (
                  <span className="text-[10px] text-slate-400 inline-flex items-center gap-1">
                    <Users className="w-3 h-3" /> {p.unique_donors} donors
                  </span>
                )}
                <ClientPortalButton entity={p} />
                <button onClick={() => activate(p.id)} data-testid={`client-open-${p.id}`}
                  className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1 ml-auto">
                  Open <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
